import { DBPosition, PositionStatus } from "../../../api/types";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import ExpandableGrid from "../../../components/ExpandableGrid";
import { Box, Grid, Typography } from "@mui/material";
import {
  differenceBetweenDates,
  dollar,
  formatDate,
  getArrFromPair,
  getPositionPnL,
  getPositionPnLDollar,
  percent,
  percentX100,
  round,
  smartRound,
} from "../../../utils/utils";
import {
  Props as DataTableProps,
  SimpleDataTable,
} from "../../../components/SimpleDataTable";

const getBgColor = (position: DBPosition): string => {
  if (position.status === "CLOSED") {
    const profitLong =
      position.openTransaction.price! < position.closeTransaction?.price!;
    const isLong = position.type === "LONG";

    const profit = isLong ? profitLong : !profitLong;

    return profit ? "#0e3f0e;" : "#611919";
  }

  if (position.status === "CANCELLED") {
    return "#3d3d3d";
  }

  if (position.status === "OPEN") {
    return "#21223d";
  }

  return "";
};

function getSummaryTable(position: DBPosition): DataTableProps {
  const openPrice = position.openTransaction.price;

  const closedPrice = position.closeTransaction?.price;

  const openCost = (position.coinAmount ?? 0) * (openPrice ?? 1);
  const openAmountWithFee = openCost - (position.openTransaction?.fee ?? 0);

  const closedAmount = (position.coinAmount ?? 0) * (closedPrice ?? 1);
  const closedAmountWithFee =
    closedAmount - (position.closeTransaction?.fee ?? 0);

  const baseSymbol = position.symbol;

  const buyPrice = position.price ? dollar(position.price) : "-";
  const closeReason = position.closeReason
    ? position.closeReason.toLowerCase().replaceAll("_", " ")
    : "Unknown?";

  // if (position.status === "CANCELLED") {
  //   return {
  //     tableData: {
  //       "Buy price:": buyPrice,
  //       "Close Reason:": closeReason,
  //     },
  //     options: {
  //       copyAllDefault: true,
  //     },
  //   };
  // }

  const investmentNativeStr = position.coinAmount
    ? smartRound(position.coinAmount)
    : "-";
  const investmentUSDStr = openAmountWithFee ? dollar(openAmountWithFee) : "-";
  const investmentSellUSDStr = closedAmountWithFee
    ? dollar(closedAmountWithFee)
    : "-";

  const tpPercent = (position.takeProfit - openPrice!) / openPrice!;
  const slPercent = (position.stopLoss - openPrice!) / openPrice!;
  const riskRewardRatio = round(Math.abs(tpPercent / slPercent), 2);

  if (position.status === "OPEN") {
    return {
      tableData: {
        "Entry price": buyPrice,
        TP: `${dollar(position.takeProfit)} (${percent(tpPercent * 100)})`,
        SL: `${dollar(position.stopLoss)} (${percent(slPercent * 100)})`,
        RR: riskRewardRatio,
        "Investment ($)*": investmentUSDStr,
        [`Investment (${baseSymbol})`]: investmentNativeStr,
      },
      options: {
        copyAllDefault: true,
      },
    };
  }

  const positionPnL = getPositionPnL(position);
  const dollarPnL = getPositionPnLDollar(position);

  const sellPrice = dollar(position.closedPrice);
  const walletEquityAfterClose = dollar(position.closeTransaction?.totalEquity);
  const portfolioProfitPercentStr =
    position.percentOfportfolio && positionPnL
      ? percent(position.percentOfportfolio * positionPnL, 2)
      : "-";

  const positionPnLExFee = getPositionPnL(position, false);

  if (position.status === "CANCELLED") {
    return {
      tableData: {
        "Entry price:": buyPrice,
        "Close Reason:": closeReason,
        RR: riskRewardRatio,
        "Entry/exit amount *": `${investmentUSDStr} / ${investmentSellUSDStr}`,
      },
      options: {
        copyAllDefault: true,
      },
    };
  }

  return {
    tableData: {
      "profit %*": percentX100(positionPnL),
      "profit %* (exc fee)": percentX100(positionPnLExFee),
      "Entry/exit price": `${buyPrice} / ${sellPrice}`,
      "Original TP": `${dollar(position.takeProfit)} (${percent(
        tpPercent * 100,
      )})`,
      "Original SL": `${dollar(position.stopLoss)} (${percent(
        slPercent * 100,
      )})`,
      RR: riskRewardRatio,

      "PnL $*": dollar(dollarPnL),
      "Portfolio PnL %*": portfolioProfitPercentStr,

      "Entry/exit amount *": `${investmentUSDStr} / ${investmentSellUSDStr}`,
      [`Investment (${baseSymbol})`]: investmentNativeStr,
      "Close Reason": closeReason,
      "Total balance after sell": walletEquityAfterClose,
      "Closed timestamp": position.closeTransaction?.filledTimestamp
        ? formatDate(
            new Date(position.closeTransaction?.filledTimestamp),
            "y-m-d h:i:s",
          )
        : "",
    },
    options: {
      copyAllDefault: true,
    },
  };
}

const statusToSymbol = (status: PositionStatus): string => {
  if (status === "PENDING") return "...";
  if (status === "OPEN") return "●";
  if (status === "CLOSED") return "✓";
  if (status === "CANCELLED") return "✕";

  return "?";
};

const getPositionOpenForStr = (position: DBPosition): string => {
  const startTimestamp = position.openPositionTimestamp;
  const endTimestamp = position.closeTransaction?.filledTimestamp;
  if (!startTimestamp || !endTimestamp) {
    return "";
  }

  const start = new Date(startTimestamp);
  const end = new Date(endTimestamp);

  return differenceBetweenDates(start, end);
};

export default function Position({ position }: { position: DBPosition }) {
  const openPrice = position.openTransaction.price;
  const closedPrice = position.closeTransaction?.price;
  const isClosed = position.status === "CLOSED"; // "PENDING" | "OPEN" | "CLOSED" | "CANCELLED"
  let percentageProfit = isClosed
    ? (closedPrice! - openPrice!) / openPrice!
    : 0;
  percentageProfit =
    position.type === "LONG" ? percentageProfit : -percentageProfit;

  const openFor = getPositionOpenForStr(position);
  const openForStr = openFor ? `(${openFor})` : "";
  const openPositionTimestampStr = position.openPositionTimestamp
    ? formatDate(new Date(position.openPositionTimestamp), "m-d h:i:s")
    : "";
  const rightSideText = [openForStr, openPositionTimestampStr]
    .filter((v) => v)
    .join(" - ");

  const [baseSymbol] = getArrFromPair(position.symbol);

  const statusSymbol = statusToSymbol(position.status);
  const type = position.type.toLowerCase();
  const title = `${type} ${baseSymbol} ${statusSymbol}`;

  const summaryDataTable = getSummaryTable(position);

  return (
    <ExpandableGrid
      defaultOpen={false}
      content={(isOpen) =>
        (isOpen && console.log(position)) || (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: getBgColor(position),
              pl: 1,
              "&::first-letter": {
                textTransform: "capitalize",
              },
            }}
          >
            {title}
            <Box sx={{ marginLeft: "auto" }}>{rightSideText}</Box>
            <Box>
              {isOpen ? (
                <ExpandLess fontSize="large" />
              ) : (
                <ExpandMore fontSize="large" />
              )}
            </Box>
          </Box>
        )
      }
    >
      {/* {isSell ? renderSell(investment) : renderBuy(investment)} */}
      <Grid size={{ md: 12, xl: 8 }}>
        <Box sx={{ p: 1 }}>
          <SimpleDataTable {...summaryDataTable} />
          <Typography>*: Including fees</Typography>
        </Box>
      </Grid>
    </ExpandableGrid>
  );
}
